/* global rwgps */
import { genKey, CharacterMetadata, ContentBlock } from 'draft-js'
import { getPhotoBlocks } from './photos'
import { findAscents } from './hill_finder'
import { Repeat, List, Map } from 'immutable'
import { compareDates } from 'lib/utils'
import smartDate from '@qrohlf/smart-date'
import moment from 'moment'

const MAX_CLIMB_BLOCKS = 5

function getBlocksFromPhotosAndPoints ({ photos, points, ride }) {
  const ascents = findAscents(points)
  const photosWithUnix = photos.map(photo => ({ ...photo, capturedI: moment(photo.capturedAt).unix() }))
  let blocks = getPhotoBlocks(photosWithUnix).concat(
    ascents.map(hill => ({
      type: 'climb',
      highlightSlice: [hill.first_i, hill.last_i],
      deltaE: hill.delta_e,
      rideId: ride.id
    }))
    // for now, disable descents and peaks
    // findDescents(points).map(hill => ({...hill, type: 'descent'}))
    // findPeaks(points).map(i => ({i, type: 'peak'}))
  )

  const photosById = {}
  photosWithUnix.forEach(photo => { photosById[photo.id] = photo })

  // FIXME will need to change capturedI usage once it changes elsewhere
  const getTimeForBlock = block => {
    switch (block.type) {
      case 'image': return photosById[block.src].capturedI
      case 'postcard': return photosById[block.imageIds[0]].capturedI
      case 'gallery': return photosById[block.imageIds[0]].capturedI
      case 'climb': return points[block.highlightSlice[0]].time
      case 'descent': return points[block.first_i].time
      case 'peak': return points[block.i].time
    }
  }
  return blocks.sort((a, b) => getTimeForBlock(a) - getTimeForBlock(b))
}

export function generateReportBlocks (report, rides) {
  const blocks = []

  const createBlock = ({ type = 'unstyled', text = '', data = Map() }) => (new ContentBlock({
    key: genKey(),
    characterList: List(Repeat(CharacterMetadata.create(), text.length)),
    type,
    text,
    data
  }))

  const sortedRides = report.rides.sort((a, b) => compareDates(rides.get(b).get('departedAt'), rides.get(a).get('departedAt')))

  sortedRides.forEach(rideId => {
    const ride = rides.get(rideId).toJS()

    if (sortedRides.length > 1) {
      blocks.push(createBlock({
        type: 'header-two',
        text: ride.name
      }))
    }

    blocks.push(createBlock({
      text: smartDate(ride.departedAt, { includeYear: true, includeTime: true })
    }))

    if (ride.description) {
      blocks.push(createBlock({
        text: ride.description
      }))
    }

    if (ride.trackPoints.length > 1) {
      blocks.push(createBlock({
        type: 'atomic',
        data: Map({
          type: 'map',
          rideId: rideId,
          sampleGraph: ride.elvationGain > 100,
          showMetrics: true
        })
      }))
    }

    const extraBlocks = getBlocksFromPhotosAndPoints({
      ride: ride,
      photos: ride.photos.map(p => p.toJS()),
      points: rwgps.TrackPoint.fromJSON(ride.rawPoints).trackPoints
    })

    extraBlocks.forEach(blockData => {
      const data = Map(blockData)
      blocks.push(createBlock({
        type: 'atomic',
        data
      }))
    })
  })
  // we need an empty block at the end
  blocks.push(createBlock({
    type: 'unstyled',
    text: ''
  }))

  // find the MAX_CLIMB_BLOCKS tallest hills in the generated blocks
  const hillsToKeep = blocks
    .filter(b => b.getIn(['data', 'type']) === 'climb')
    .sort((a, b) => b.getIn(['data', 'deltaE']) - a.getIn(['data', 'deltaE']))
    .slice(0, MAX_CLIMB_BLOCKS)
    .map(b => b.getKey())

  // strip out the rest, without messing with non-climb blocks
  const discardHills = (b) => b.getIn(['data', 'type']) !== 'climb' || hillsToKeep.includes(b.getKey())

  return blocks.filter(discardHills)
}
